import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { message, Upload } from 'antd'
import { InboxOutlined } from '@ant-design/icons'

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

/**
 * Image upload field with drag-and-drop.
 * Does NOT upload immediately — stores file and exposes via ref.getPendingFile().
 * Parent should upload on form submit.
 * - If value (path) is set: parent uploads to that path (overwrites)
 * - If value is empty: parent uploads with no path, backend generates root/{random}.{ext}
 * @param {object} props - { value, onChange, disabled, placeholder, onFileSelect }
 */
const ImageUploadField = forwardRef(function ImageUploadField(
  { value, onChange, disabled, placeholder = 'path/to/image.jpg', onFileSelect },
  ref
) {
  const [previewUrl, setPreviewUrl] = useState(null)
  const pendingFileRef = useRef(null)
  const previewUrlRef = useRef(null)

  useImperativeHandle(ref, () => ({
    getPendingFile: () => pendingFileRef.current,
    clearPendingFile: () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = null
      }
      pendingFileRef.current = null
      setPreviewUrl(null)
    },
  }))

  const handleFile = (file) => {
    const f = file.originFileObj ?? file
    if (!IMAGE_TYPES.includes(f.type)) {
      message.error('يرجى اختيار صورة (jpg, png, gif, webp)')
      return false
    }
    if (f.size > 5 * 1024 * 1024) {
      message.error('الحد الأقصى لحجم الصورة 5 ميجابايت')
      return false
    }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    pendingFileRef.current = f
    const url = URL.createObjectURL(f)
    previewUrlRef.current = url
    setPreviewUrl(url)
    onChange?.(undefined)
    onFileSelect?.(f)
    return false
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Upload.Dragger
        name="file"
        multiple={false}
        accept={IMAGE_TYPES.join(',')}
        showUploadList={false}
        disabled={disabled}
        beforeUpload={handleFile}
        style={{
          padding: 24,
          border: '2px dashed #d9d9d9',
          borderRadius: 8,
          background: '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
        </p>
        <p className="ant-upload-text">
          اسحب الصورة هنا أو انقر للاختيار (تُرفع عند الحفظ)
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12, color: '#999' }}>
          JPG, PNG, GIF, WebP — حد أقصى 5 ميجابايت
        </p>
      </Upload.Dragger>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {(value || previewUrl) && (
          <div style={{ width: 48, height: 48, borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: '#f0f0f0' }}>
            <img
              src={previewUrl || (value ? `https://images.leen-go.com/${value}` : null)}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        )}
        <input
          type="text"
          className="ant-input"
          value={value || ''}
          onChange={(e) => {
            if (previewUrlRef.current) {
              URL.revokeObjectURL(previewUrlRef.current)
              previewUrlRef.current = null
            }
            pendingFileRef.current = null
            setPreviewUrl(null)
            onChange?.(e.target.value || undefined)
          }}
          placeholder={placeholder}
          disabled={disabled}
          style={{ flex: 1, minWidth: 120 }}
        />
        {value && (
          <a
            href={`https://images.leen-go.com/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12 }}
          >
            معاينة
          </a>
        )}
      </div>
    </div>
  )
})

export default ImageUploadField
